/**
 * 浮动聊天按钮(全站 FAB)
 *
 * 实时在线聊天模式:
 * - 第一次点开:创建工单(后台可见)
 * - 之后在 modal 内实时对话
 * - user 发消息 → 立即显示
 * - 后台回复 → 5 秒内轮询收到
 * - 不跳转页面
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Sparkles, AlertCircle, CheckCircle2, User, Headphones, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../lib/api.js';
import { useUser } from '../stores/user.js';

export default function ChatWidget() {
  const { user } = useUser();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');

  // 工单状态
  const [ticketId, setTicketId] = useState(null);
  const [messages, setMessages] = useState([]); // [{sender_type, content, created_at}]
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollTimerRef = useRef(null);

  // 24h 消息过滤(隐私:24h 前的消息自动隐藏)
  const MESSAGE_TTL_MS = 24 * 3600 * 1000;
  const filterFresh = (msgs) => {
    const cutoff = Date.now() - MESSAGE_TTL_MS;
    return (msgs || []).filter(m => {
      const t = m.created_at ? (m.created_at > 1e12 ? m.created_at : m.created_at * 1000) : 0;
      return t >= cutoff;
    });
  };

  // 自动滚动到底部
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  // 加载工单消息
  const loadMessages = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const res = await api.support.ticketDetail(tid);
      if (res?.ok) {
        setMessages(filterFresh(res.messages || []));
        scrollToBottom();
      }
    } catch (e) {
      console.warn('[客服] 拉消息失败:', e.message);
    }
  }, [scrollToBottom]);

  // 轮询新消息(每 4 秒)
  useEffect(() => {
    if (!open || !ticketId) return;
    loadMessages(ticketId);
    pollTimerRef.current = setInterval(() => {
      loadMessages(ticketId);
    }, 4000);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [open, ticketId, loadMessages]);

  // 打开 modal 时,自动创建/查找活跃工单
  useEffect(() => {
    if (!open) return;
    if (ticketId) return;
    setLoading(true);
    setStatus(null);
    setStatusMsg('');
    (async () => {
      try {
        const list = await api.support.tickets({ limit: 20 });
        let tid = null;
        if (list?.ok && list.tickets?.length > 0) {
          const active = list.tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved');
          if (active.length > 0) {
            active.sort((a, b) => (b.last_reply_at || b.updated_at || 0) - (a.last_reply_at || a.updated_at || 0));
            tid = active[0].id;
          }
        }
        if (!tid) {
          const res = await api.support.createTicket({
            category: '产品',
            subject: `在线咨询 - ${user.phone?.slice(-4) || '用户'}`,
            content: '我想咨询一些问题',
            priority: 'normal',
          });
          if (res?.ok && res.id) tid = res.id;
        }
        if (tid) {
          setTicketId(tid);
          await loadMessages(tid);
        } else {
          setStatus('error');
          setStatusMsg('无法创建对话,请稍后再试');
        }
      } catch (e) {
        setStatus('error');
        setStatusMsg(e.message || '连接失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, ticketId, user, loadMessages]);

  // 发送消息
  const handleSend = async () => {
    const content = message.trim();
    if (!content || sending || !ticketId) return;
    setSending(true);

    // 乐观更新(立即显示)
    const tempMsg = {
      id: `temp-${Date.now()}`,
      sender_type: 'user',
      content,
      created_at: Date.now(),
      _pending: true,
    };
    setMessages(prev => [...prev, tempMsg]);
    setMessage('');
    scrollToBottom();

    try {
      const res = await api.support.ticketReply(ticketId, content);
      if (res?.ok) {
        // 拉取最新(确保显示 + 去掉 pending)
        await loadMessages(ticketId);
      } else {
        // 标记发送失败
        setMessages(prev =>
          prev.map(m => m.id === tempMsg.id ? { ...m, _error: true } : m)
        );
        toast.error(res?.error || '发送失败');
      }
    } catch (e) {
      setMessages(prev =>
        prev.map(m => m.id === tempMsg.id ? { ...m, _error: true } : m)
      );
      toast.error(e.message || '网络错误');
    } finally {
      setSending(false);
    }
  };

  // Enter 发送,Shift+Enter 换行
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // 关闭 modal
  const handleClose = () => {
    if (sending) return;
    setOpen(false);
    // 保留 ticketId,下次打开继续聊
  };

  // 时间格式化
  const formatTime = (ts) => {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  // 在 /admin / /support 隐藏,或者未登录 — 必须在所有 hooks 之后!
  if (location.pathname.startsWith('/admin')) return null;
  if (location.pathname === '/support') return null;
  if (!user) return null;

  return (
    <>
      {/* 浮动按钮 */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => (open ? handleClose() : setOpen(true))}
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center"
        style={{
          background: open
            ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
            : 'linear-gradient(135deg, #22d3ee 0%, #a855f7 100%)',
        }}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div
              key="x"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X size={24} className="text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="msg"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="relative"
            >
              <MessageCircle size={24} className="text-white" />
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-400 border-2 border-bg-base animate-pulse"></span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* 弹窗 */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40 md:hidden"
              onClick={handleClose}
            />
            <motion.div
              initial={{ y: 100, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 100, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed bottom-36 right-4 md:bottom-24 md:right-6 z-40 w-[calc(100vw-2rem)] max-w-sm glass-strong rounded-2xl border border-white/10 overflow-hidden shadow-2xl flex flex-col"
              style={{ maxHeight: '70vh', height: '500px' }}
            >
              {/* Header */}
              <div
                className="p-4 relative flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #22d3ee 0%, #a855f7 100%)' }}
              >
                <button
                  onClick={handleClose}
                  disabled={sending}
                  className="absolute top-3 right-3 text-white/80 hover:text-white disabled:opacity-50"
                >
                  <X size={18} />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Headphones size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-white font-bold flex items-center gap-2">
                      在线客服
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/20 text-white flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse"></span>
                        {ticketId ? `#${ticketId}` : '连接中...'}
                      </span>
                    </div>
                    <div className="text-white/80 text-xs mt-0.5">工作日 9:00-22:00 在线</div>
                    <div className="text-white/60 text-[10px] mt-0.5 flex items-center gap-1">
                      <Clock size={9} /> 消息保留 24 小时,过期自动清除
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages 区域 */}
              <div className="flex-1 overflow-y-auto p-3 bg-bg-2 space-y-2">
                {loading && !ticketId ? (
                  <div className="text-center py-8 text-ink-3 text-xs">
                    <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
                    正在连接客服...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-ink-3 text-xs">
                    <MessageCircle size={28} className="mx-auto mb-2 opacity-30" />
                    暂无消息,开始聊天吧
                  </div>
                ) : (
                  messages.map((m) => (
                    <MessageBubble key={m.id || m._id} msg={m} formatTime={formatTime} />
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* 状态反馈 */}
              {status === 'error' && (
                <div
                  className="px-3 py-2 text-xs flex items-center gap-2 flex-shrink-0"
                  style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', borderTop: '1px solid rgba(239,68,68,0.3)' }}
                >
                  <AlertCircle size={14} />
                  {statusMsg}
                </div>
              )}

              {/* Input 区域 */}
              <div className="p-3 border-t border-white/5 bg-bg-card flex-shrink-0">
                <div className="flex items-end gap-2">
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="输入消息... (Enter 发送)"
                    rows={1}
                    maxLength={500}
                    disabled={!ticketId || sending}
                    className="flex-1 bg-bg-elev border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-cyan/40 resize-none disabled:opacity-50"
                    style={{ minHeight: '36px', maxHeight: '80px' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!message.trim() || !ticketId || sending}
                    className="btn-primary !p-2 !rounded-lg disabled:opacity-50 flex-shrink-0"
                  >
                    {sending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[10px] text-ink-3">
                  <span>实时对话,客服回复会自动显示</span>
                  <span>{message.length}/500</span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function MessageBubble({ msg, formatTime }) {
  const isUser = msg.sender_type === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx('flex gap-2', isUser ? 'justify-end' : 'justify-start')}
    >
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-brand-cyan/20 flex items-center justify-center flex-shrink-0">
          <Headphones size={14} className="text-brand-cyan" />
        </div>
      )}
      <div className={clsx('max-w-[70%] flex flex-col', isUser && 'items-end')}>
        <div
          className={clsx(
            'rounded-2xl px-3 py-2 text-sm break-words',
            isUser
              ? 'bg-gradient-to-br from-brand-cyan/30 to-brand-purple/30 text-white border border-brand-cyan/20'
              : 'bg-bg-elev text-ink-1 border border-white/5',
            msg._error && 'opacity-50',
            msg._pending && 'opacity-70'
          )}
        >
          {msg.content}
        </div>
        <div className="text-[10px] text-ink-3 mt-0.5 px-1">
          {msg._pending ? '发送中...' : msg._error ? '发送失败' : formatTime(msg.created_at)}
        </div>
      </div>
      {isUser && (
        <div className="w-7 h-7 rounded-full bg-brand-purple/20 flex items-center justify-center flex-shrink-0">
          <User size={14} className="text-brand-purple" />
        </div>
      )}
    </motion.div>
  );
}
